import React, { useState } from 'react';
import { AlertTriangle, Send, Loader } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import apiService from '../../services/apiService';
import MediaUpload from '../common/MediaUpload';
import MediaCapture from '../common/MediaCapture';
import AudioRecorder from '../common/AudioRecorder';
import IssueTypeSelector from '../common/IssueTypeSelector';

const ReportForm = ({
  bedData,
  farmData,
  plotData,
  // River Monitor props
  pointData,
  watershedData,
  siteData,
  onSuccess,
  onCancel,
  className = ''
}) => {
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Determine report type and data source (support both old and new terminology)
  const reportData = bedData || farmData || plotData || pointData || watershedData || siteData;

  // Check if bedData contains river monitoring structure (point/watershed/site) or legacy structure (bed/farm/plot)
  const hasPointStructure = bedData && bedData.point;
  const hasBedStructure = bedData && bedData.bed;

  const isBedReport = hasBedStructure;
  const isFarmReport = !!farmData && !bedData && !plotData;
  const isPlotReport = !!plotData && !bedData;

  // River Monitor report types
  const isPointReport = !!pointData || hasPointStructure;
  const isWatershedReport = !!watershedData && !pointData && !siteData && watershedData?.watershed;
  const isSiteReport = !!siteData && !pointData && siteData?.site;

  // Form state
  const [formData, setFormData] = useState({
    description: '',
    notes: '',
    selectedIssues: [],
    customIssues: [],
    uploadedFiles: [],
    capturedFiles: [],
    audioFile: null
  });

  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};

    // At least one field should be filled
    const hasContent = formData.description.trim() ||
                      formData.notes.trim() ||
                      formData.selectedIssues.length > 0 ||
                      formData.customIssues.length > 0 ||
                      formData.uploadedFiles.length > 0 ||
                      formData.capturedFiles.length > 0 ||
                      formData.audioFile;

    if (!hasContent) {
      newErrors.general = 'Please provide at least some information about the issue';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear field error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const handleIssueSelection = ({ selectedIssues, customIssues }) => {
    setFormData(prev => ({
      ...prev,
      selectedIssues,
      customIssues
    }));
  };

  const uploadMediaFiles = async (submissionId) => {
    const uploadPromises = [];

    // Upload uploaded media files
    formData.uploadedFiles.forEach(file => {
      const fileType = file.type.startsWith('image/') ? 'image' : 'video';
      uploadPromises.push(
        apiService.uploadMedia(file, fileType, submissionId)
      );
    });

    // Upload captured media files
    formData.capturedFiles.forEach(file => {
      const fileType = file.type.startsWith('image/') ? 'image' : 'video';
      uploadPromises.push(
        apiService.uploadMedia(file, fileType, submissionId)
      );
    });

    // Upload audio file
    if (formData.audioFile) {
      uploadPromises.push(
        apiService.uploadMedia(formData.audioFile, 'audio', submissionId)
      );
    }

    try {
      const results = await Promise.all(uploadPromises);
      console.log('Media upload results:', results);
      return results;
    } catch (error) {
      console.error('Media upload error:', error);
      throw new Error('Failed to upload media files');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      let submissionData;

      if (isBedReport) {
        // Point-level report
        const { bed, farm, plot } = bedData;
        submissionData = {
          point_id: bed.id,
          watershed_id: farm.id,
          site_id: plot.id,
          report_type: 'Point',
          title: `Point Issue: ${bed.point_id || bed.id}`,
          description: formData.description.trim() || '',
          additional_notes: formData.notes.trim() || '',
          categories: formData.selectedIssues,
          custom_tags: formData.customIssues,
          severity: 'Medium', // Default severity
          priority: 'P2', // Default priority
          observer_name: user?.name || user?.email || 'Anonymous',
          observation_date: new Date().toISOString(),
          media_urls: [],
          audio_url: ''
        };
      } else if (isPlotReport) {
        // Site-level report
        const { farm, plot } = plotData;
        submissionData = {
          watershed_id: farm.id,
          site_id: plot.id,
          report_type: 'Site',
          title: `Site Issue: ${plot.name}`,
          description: formData.description.trim() || '',
          additional_notes: formData.notes.trim() || '',
          categories: formData.selectedIssues,
          custom_tags: formData.customIssues,
          severity: 'Medium',
          priority: 'P2',
          observer_name: user?.name || user?.email || 'Anonymous',
          observation_date: new Date().toISOString(),
          media_urls: [],
          audio_url: ''
        };
      } else if (isFarmReport) {
        // Watershed-level report
        const { farm } = farmData;
        submissionData = {
          watershed_id: farm.id,
          report_type: 'Watershed',
          title: `Watershed Issue: ${farm.name}`,
          description: formData.description.trim() || '',
          additional_notes: formData.notes.trim() || '',
          categories: formData.selectedIssues,
          custom_tags: formData.customIssues,
          severity: 'Medium',
          priority: 'P2',
          observer_name: user?.name || user?.email || 'Anonymous',
          observation_date: new Date().toISOString(),
          media_urls: [],
          audio_url: ''
        };
      } else if (isPointReport) {
        // River Monitor: Point-level report
        // Handle both pointData prop and bedData with point structure
        const data = pointData || bedData;
        const { point, watershed, site } = data;
        submissionData = {
          point_id: point.id,
          watershed_id: watershed.id,
          site_id: site.id,
          report_type: 'Point',
          title: `Point Issue: ${point.parameter || point.point_id}`,
          description: formData.description.trim() || '',
          additional_notes: formData.notes.trim() || '',
          categories: formData.selectedIssues,
          custom_tags: formData.customIssues,
          severity: 'Medium',
          priority: 'P2',
          observer_name: user?.name || user?.email || 'Anonymous',
          observation_date: new Date().toISOString(),
          media_urls: [],
          audio_url: ''
        };
      } else if (isSiteReport) {
        // River Monitor: Site-level report
        const { farm, site } = siteData || {};
        if (!site || !farm) {
          throw new Error('Site or watershed data is missing');
        }
        submissionData = {
          watershed_id: farm.id,
          site_id: site.id,
          report_type: 'Site',
          title: `Site Issue: ${site.name}`,
          description: formData.description.trim() || '',
          additional_notes: formData.notes.trim() || '',
          categories: formData.selectedIssues,
          custom_tags: formData.customIssues,
          severity: 'Medium',
          priority: 'P2',
          observer_name: user?.name || user?.email || 'Anonymous',
          observation_date: new Date().toISOString(),
          media_urls: [],
          audio_url: ''
        };
      } else if (isWatershedReport) {
        // River Monitor: Watershed-level report
        const { watershed } = watershedData;
        submissionData = {
          watershed_id: watershed.id,
          report_type: 'Watershed',
          title: `Watershed Issue: ${watershed.name}`,
          description: formData.description.trim() || '',
          additional_notes: formData.notes.trim() || '',
          categories: formData.selectedIssues,
          custom_tags: formData.customIssues,
          severity: 'Medium',
          priority: 'P2',
          observer_name: user?.name || user?.email || 'Anonymous',
          observation_date: new Date().toISOString(),
          media_urls: [],
          audio_url: ''
        };
      } else {
        throw new Error('Invalid report data: bedData, plotData, farmData, pointData, siteData, or watershedData must be provided');
      }

      console.log('Creating submission with data:', submissionData);

      // Create submission
      const submissionResponse = await apiService.createSubmission(submissionData);

      if (!submissionResponse.success) {
        throw new Error(submissionResponse.message || 'Failed to create submission');
      }

      const submissionId = submissionResponse.data.id;
      console.log('Submission created with ID:', submissionId);

      // Upload media files if any
      if (formData.uploadedFiles.length > 0 || formData.capturedFiles.length > 0 || formData.audioFile) {
        try {
          await uploadMediaFiles(submissionId);
          toast.success('Report submitted with media files');
        } catch (uploadError) {
          console.warn('Media upload failed, but submission was created:', uploadError);
          toast.success('Report submitted (media upload failed - you can retry uploading files later)');
        }
      } else {
        toast.success('Report submitted successfully');
      }

      // Reset form
      setFormData({
        description: '',
        notes: '',
        selectedIssues: [],
        customIssues: [],
        uploadedFiles: [],
        capturedFiles: [],
        audioFile: null
      });

      if (onSuccess) {
        onSuccess(submissionResponse.data);
      }

    } catch (error) {
      console.error('Report submission error:', error);
      toast.error(error.message || 'Failed to submit report. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={className}>
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Header */}
        <div className="border-b border-gray-200 pb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <AlertTriangle className="h-5 w-5 text-orange-500 mr-2" />
            {isBedReport && `Report Issue for ${bedData.bed.crop} Bed`}
            {isPlotReport && `Report Issue for ${plotData.plot.name}`}
            {isFarmReport && `Report Issue for ${farmData.farm.name}`}
            {isPointReport && !pointData && bedData && bedData.point && `Report Issue for Point ${bedData.point.point_id || bedData.point.parameter}`}
            {isPointReport && pointData && `Report Issue for ${pointData?.point?.parameter || 'Point'}`}
            {isSiteReport && `Report Issue for ${siteData?.site?.name || 'Site'}`}
            {isWatershedReport && `Report Issue for ${watershedData?.watershed?.name || 'Watershed'}`}
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            {isBedReport && 'Report any issues with this bed. All fields are optional, but please provide as much detail as possible.'}
            {isPlotReport && 'Report any issues with this plot. All fields are optional, but please provide as much detail as possible.'}
            {isFarmReport && 'Report any issues with this farm. All fields are optional, but please provide as much detail as possible.'}
            {isPointReport && 'Report any observations or issues with this monitoring point. All fields are optional, but please provide as much detail as possible.'}
            {isSiteReport && 'Report any observations or issues with this site. All fields are optional, but please provide as much detail as possible.'}
            {isWatershedReport && 'Report any observations or issues with this watershed. All fields are optional, but please provide as much detail as possible.'}
          </p>
        </div>

        {/* General Error */}
        {errors.general && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-sm text-red-600">{errors.general}</p>
          </div>
        )}

        {/* Issue Types */}
        <IssueTypeSelector
          selectedIssues={formData.selectedIssues}
          customIssues={formData.customIssues}
          onSelectionChange={handleIssueSelection}
        />

        {/* Description */}
        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
            Issue Description
          </label>
          <textarea
            id="description"
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            placeholder="Describe the issue you've observed..."
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
          />
        </div>

        {/* Notes */}
        <div>
          <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">
            Additional Notes
          </label>
          <textarea
            id="notes"
            value={formData.notes}
            onChange={(e) => handleInputChange('notes', e.target.value)}
            placeholder="Any additional observations, suggestions, or context..."
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
          />
        </div>

        {/* Media Upload - Removed duplicate, using MediaCapture instead */}
        {/* <MediaUpload
          onFilesChange={(files) => handleInputChange('uploadedFiles', files)}
          maxFileSize={50 * 1024 * 1024} // 50MB
          maxVideoLength={30} // 30 seconds
          label="Upload Photos & Videos"
        /> */}

        {/* Media Capture - Handles both upload and camera capture */}
        <MediaCapture
          onFilesChange={(files) => handleInputChange('capturedFiles', files)}
          maxFileSize={50 * 1024 * 1024} // 50MB
          maxVideoLength={30} // 30 seconds
          label="Upload or Capture Photos & Videos"
        />

        {/* Audio Recorder */}
        <AudioRecorder
          onAudioChange={(audio) => handleInputChange('audioFile', audio)}
          maxDuration={60} // 1 minute
        />

        {/* Report Information */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Reporting for:</h4>
          <div className="text-sm text-gray-600 space-y-1">
            {isBedReport && (
              <>
                <p><span className="font-medium">Bed:</span> {bedData.bed.crop} Bed (ID: {bedData.bed.id})</p>
                <p><span className="font-medium">Plot:</span> {bedData.plot.name}</p>
                <p><span className="font-medium">Farm:</span> {bedData.farm.name}</p>
              </>
            )}
            {isPlotReport && (
              <>
                <p><span className="font-medium">Plot:</span> {plotData.plot.name} (ID: {plotData.plot.id})</p>
                <p><span className="font-medium">Farm:</span> {plotData.farm.name}</p>
                {plotData.plot.description && <p><span className="font-medium">Description:</span> {plotData.plot.description}</p>}
                {plotData.plot.area && <p><span className="font-medium">Area:</span> {plotData.plot.area} sq ft</p>}
              </>
            )}
            {isFarmReport && (
              <>
                <p><span className="font-medium">Farm:</span> {farmData.farm.name} (ID: {farmData.farm.id})</p>
                {farmData.farm.owner && <p><span className="font-medium">Owner:</span> {farmData.farm.owner}</p>}
                {farmData.farm.address && <p><span className="font-medium">Location:</span> {farmData.farm.address}</p>}
              </>
            )}
            {isPointReport && pointData && (
              <>
                <p><span className="font-medium">Point:</span> {pointData.point.parameter} Point (ID: {pointData.point.id})</p>
                <p><span className="font-medium">Site:</span> {pointData.site.name}</p>
              </>
            )}
            {isPointReport && !pointData && bedData && bedData.point && (
              <>
                <p><span className="font-medium">Point:</span> {bedData.point.point_id || bedData.point.parameter} (ID: {bedData.point.id})</p>
                <p><span className="font-medium">Site:</span> {bedData.site.name}</p>
                <p><span className="font-medium">Watershed:</span> {bedData.watershed.name}</p>
              </>
            )}
            {isSiteReport && (
              <>
                <p><span className="font-medium">Site:</span> {siteData?.site?.name || 'N/A'} (ID: {siteData?.site?.id || 'N/A'})</p>
                <p><span className="font-medium">Watershed:</span> {siteData?.farm?.name || 'N/A'}</p>
                {siteData?.site?.description && <p><span className="font-medium">Description:</span> {siteData.site.description}</p>}
                {siteData?.site?.waterbody_type && <p><span className="font-medium">Waterbody Type:</span> {siteData.site.waterbody_type}</p>}
              </>
            )}
            {isWatershedReport && (
              <>
                <p><span className="font-medium">Watershed:</span> {watershedData.watershed.name} (ID: {watershedData.watershed.id})</p>
                {watershedData.watershed.created_by && <p><span className="font-medium">Created By:</span> {watershedData.watershed.created_by}</p>}
                {watershedData.watershed.address && <p><span className="font-medium">Location:</span> {watershedData.watershed.address}</p>}
              </>
            )}
            <p><span className="font-medium">Reporter:</span> {user?.name || user?.email || 'Anonymous'}</p>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>

          <button
            type="submit"
            disabled={isSubmitting}
            className="px-6 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {isSubmitting ? (
              <>
                <Loader className="h-4 w-4 mr-2 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Submit Report
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ReportForm;